import { useRouter } from 'next/navigation';
import Typography from '@/components/atoms/typography/typography';
import Dropdown, {
  DropdownItem,
} from '@/components/molecules/dropdown/dropdown';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import DurationSelectorItem, {
  DurationSelectorItemProps,
} from './duration-selector-item/duration-selector-item';
import styles from './duration-selector.module.scss';

export interface DurationSelectorProps {
  headline?: string;
  /** The count of how many items to show before the rest go into a dropdown menu */
  showCount?: number;
  /** The duration items to display */
  tabs: Content.Components['DurationSelectorTab'][];
}

const DurationSelector = ({
  headline,
  tabs = [],
  showCount = 3,
}: DurationSelectorProps) => {
  const router = useRouter();
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();

  const durationOptions: DurationSelectorItemProps[] = tabs.map(tab => ({
    label: `${tab.offer.daysDuration} ${t('items.common.words.days')}`,
    state: tab.isSelected ? 'active' : 'default',
    tag: tab.isBestOffer ? t('items.common.words.bestDeal') : undefined,
    text: currencyFormatter(tab.offer.price.totalPrice),
    // ! NOTE: talked with Kristian from BE, this is the URL the buttons should redirect to, may need to be changed at some point.
    url: tab.offer.searchUrl,
  }));

  const durationsToShow = durationOptions.slice(0, showCount);

  // Map extra items to dropdown options
  const extraDurations: DropdownItem[] = durationOptions
    .slice(showCount)
    .map(item => ({
      label: item.label,
      subText: item.text,
      value: item.url,
    }));

  const handleChange = (url: string) => {
    if (!url) return;

    router.push(url);
  };

  const activeDuration = tabs.find(tab => tab.isSelected)?.offer.searchUrl;

  return (
    <div className={styles.component}>
      {headline && (
        <Typography className={styles.headline} tagStyle="bodyMediumBold">
          {headline}
        </Typography>
      )}

      <div className={styles.durations}>
        {durationsToShow.map((duration, durationIndex) => (
          <DurationSelectorItem
            key={durationIndex}
            className={styles.duration}
            {...duration}
          />
        ))}

        {extraDurations.length > 0 && (
          <Dropdown
            className={styles.duration}
            items={extraDurations}
            label={t('items.common.words.seeOtherDates')}
            onChange={handleChange}
            value={activeDuration}
          />
        )}
      </div>
    </div>
  );
};

DurationSelector.displayName = 'DurationSelector';

export default DurationSelector;
